import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { Button } from "components/Button"
import { cdn } from "core/cdn"
import { DoctorsResults, MIN_SEARCH_LENGTH } from "containers/DoctorSearch/DoctorSearch"
import {
  DOCTOR_PROFESSION,
  RESET_DOCTOR_LIST,
  SEARCH_DOCTORS,
} from "containers/DoctorSearch/services/constants"
import { Keyboard } from "hocs/withKeyboard"
import { Input } from "components/Input"
import { useLanguage } from "locales"
import { Customer, FamilyDoctor, GenderDoctor } from "types/entity"
import { DoctorSearchStore } from "types/store"

import { SectionHeader } from "./SectionHeader"

type Mode = "view" | "search"

export interface TreatingDoctorProps {
  loading: boolean
  patient: Customer
  change: (p: Partial<Customer>) => void
  onBack: () => void
}

const isFemaleDoctor = (doctor: FamilyDoctor) => doctor.sex === GenderDoctor.FEMALE

export const TreatingDoctor: React.FC<TreatingDoctorProps> = (props) => {
  const { languages } = useLanguage()
  const dispatch = useDispatch()
  const [mode, setMode] = useState<Mode>("view")
  const [request, setRequest] = useState("")
  const [isPendingSearch, setIsPendingSearch] = useState(false)
  const { doctors = [], hasMore = false, currentPage = 1, loading: searchLoading = false } = useSelector(
    ({ searchedDoctors }: { searchedDoctors: DoctorSearchStore }) => searchedDoctors
  )

  const familyDoctor = props.patient.family_doctor
  const hasDoctor = Object.keys(familyDoctor ?? {}).length > 0

  useEffect(() => {
    if (mode !== "search") return
    if (request.length >= MIN_SEARCH_LENGTH) {
      setIsPendingSearch(true)
      const delay = setTimeout(() => {
        setIsPendingSearch(false)
        dispatch({
          type: SEARCH_DOCTORS,
          payload: { fullname: request.trim(), profession: DOCTOR_PROFESSION },
        })
      }, 200)
      return () => clearTimeout(delay)
    } else {
      setIsPendingSearch(false)
      dispatch({ type: RESET_DOCTOR_LIST })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, mode])

  const cancel = () => {
    dispatch({ type: RESET_DOCTOR_LIST })
    setRequest("")
    setMode("view")
  }

  const selectDoctor = (doctor: FamilyDoctor) => {
    props.change({ family_doctor_rpps: doctor.rpps })
    dispatch({ type: RESET_DOCTOR_LIST })
    setMode("view")
  }

  const openAdd = () => {
    setRequest(hasDoctor ? familyDoctor.lastname : "")
    setMode("search")
  }

  const removeDoctor = () => {
    props.change({ family_doctor_rpps: "" })
  }

  const onNearEnd = hasMore && !searchLoading ? () => {
    dispatch({
      type: SEARCH_DOCTORS,
      payload: { fullname: request.trim(), profession: DOCTOR_PROFESSION, page: currentPage + 1 },
    })
  } : undefined

  if (mode === "search") {
    // Fixed height so paging through results doesn't push the keyboard up or down.
    const results = (
      <div style={{ height: 110 }}>
        <DoctorsResults doctors={doctors} onNearEnd={onNearEnd} query={request} loading={searchLoading || isPendingSearch} selectDoctor={selectDoctor} />
      </div>
    )
    return (
      <>
        <SectionHeader onBack={cancel} title={languages.treatingDoctorResultsTitle} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginTop: 20, gap: 20 }}>
          <Keyboard
            options={{ type: "text", inputName: "request", resultsDisplay: results }}
            value={request}
            onChange={(value) => setRequest(value as string)}
            containerStyle={{ width: 1450, minHeight: "auto" }}
          >
            <Input
              placeholder={languages.requestDoctorPlaceholder}
              style={{ marginBottom: "50px" }}
              value={request}
              name="request"
            />
          </Keyboard>
        </div>
      </>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: 900 }}>
      <SectionHeader onBack={props.onBack} title={languages.treatingDoctor} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 80 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 30 }}>
          <img
            src={cdn(
              !hasDoctor
                ? "/images/picto_no_doctor.svg"
                : isFemaleDoctor(familyDoctor as FamilyDoctor)
                ? "/images/female_doctor.svg"
                : "/images/ps_picto.svg"
            )}
            style={{ width: 150, height: 150 }}
            alt=""
          />
          {hasDoctor ? (
            <span style={{ fontFamily: "Now", fontSize: 42, color: "#5A5A5A", width: 728 }}>
              {familyDoctor.firstname} {familyDoctor.lastname}
              <br />
              {familyDoctor.libelle_commune_coord_structure || languages.addressNotProvided}
            </span>
          ) : (
            <span style={{ fontFamily: "Now", fontSize: 42, color: "#5A5A5A", width: 728 }}>
              {languages.noTreatingDoctorSet}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 50 }}>
          {hasDoctor && (
            <Button
              htmlType="button"
              loading={props.loading}
              onClick={removeDoctor}
              wide="long"
              style={{
                background: "#fff",
                color: "#D64949",
                padding: "30px 102px",
                borderRadius: 4,
                fontFamily: "Now",
                fontWeight: 500,
                fontSize: 38,
                height: "auto",
                border: "2px solid #D64949",
              }}
            >
              {languages.delete}
            </Button>
          )}
          <Button
            htmlType="button"
            loading={props.loading}
            onClick={openAdd}
            wide="long"
            style={{
              background: "#1D71B8",
              color: "#fff",
              padding: "30px 102px",
              borderRadius: 4,
              fontFamily: "Now",
              fontWeight: 500,
              fontSize: 38,
              height: "auto",
              border: "none",
            }}
          >
            {hasDoctor ? languages.modify : languages.add}
          </Button>
        </div>
      </div>
    </div>
  )
}
